import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit, Trash2, Users, Shield, ShieldOff, Eye, EyeOff } from 'lucide-react';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: true,
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({ username: '', email: '', password: '', is_admin: true });
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      is_admin: user.is_admin,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          email: formData.email,
          is_admin: formData.is_admin,
        };
        if (formData.password) updateData.password = formData.password;
        await axios.put(`${API}/analytics/users/${editingUser.id}`, updateData, { headers });
        toast.success('User updated successfully');
      } else {
        if (!formData.password) { toast.error('Password is required'); return; }
        await axios.post(`${API}/analytics/users/create`, formData, { headers });
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/analytics/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const togglePasswordReveal = (userId) => {
    setRevealedPasswords(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const inputCls = "bg-[#0A0E27] border-white/10 text-white placeholder:text-[#475569] focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 rounded-xl";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
      <header className="bg-black/40 backdrop-blur-md border-b border-[#D4AF37]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-2xl font-bold text-[#D4AF37]" data-testid="user-mgmt-title">User Management</h1>
              <p className="text-[#C9A961] text-sm">Manage admin users and access control</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={openCreateDialog}
                className="bg-gradient-to-r from-[#D4AF37] to-[#C9A961] hover:from-[#C9A961] hover:to-[#D4AF37] text-black font-semibold" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" /> Add User
              </Button>
              <Button onClick={() => navigate('/admin/dashboard')} variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black" data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#131A38] rounded-xl border border-white/5 p-5 text-center">
            <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-3xl font-bold text-white" data-testid="total-users-count">{users.length}</p>
            <p className="text-[#94A3B8] text-xs mt-1">Total Users</p>
          </div>
          <div className="bg-[#131A38] rounded-xl border border-white/5 p-5 text-center">
            <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-3xl font-bold text-white" data-testid="admin-users-count">{users.filter(u => u.is_admin).length}</p>
            <p className="text-[#94A3B8] text-xs mt-1">Admins</p>
          </div>
          <div className="bg-[#131A38] rounded-xl border border-white/5 p-5 text-center">
            <ShieldOff className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
            <p className="text-3xl font-bold text-white">{users.filter(u => !u.is_admin).length}</p>
            <p className="text-[#94A3B8] text-xs mt-1">Non-Admins</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-16 h-16 text-[#D4AF37]/20 mx-auto mb-4" />
                <p className="text-[#94A3B8] text-lg">No users found</p>
              </div>
            ) : users.map((u) => (
              <div key={u.id} className="bg-[#131A38] rounded-xl border border-white/5 hover:border-[#D4AF37]/20 p-5 flex items-center justify-between gap-4 transition-all duration-300" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${u.is_admin ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-white/5 text-[#94A3B8]'}`}>
                    {u.username?.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{u.username}</p>
                    <p className="text-[#94A3B8] text-sm truncate">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${u.is_admin ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-white/5 text-[#94A3B8]'}`}>
                    {u.is_admin ? 'Admin' : 'User'}
                  </span>
                  <div className="flex items-center gap-1 text-[#475569] text-xs">
                    <span className="font-mono">{revealedPasswords[u.id] ? u.password_hash : '••••••••'}</span>
                    <button onClick={() => togglePasswordReveal(u.id)} className="text-[#94A3B8] hover:text-white p-1" data-testid={`reveal-pw-${u.id}`}>
                      {revealedPasswords[u.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <Button size="sm" onClick={() => openEditDialog(u)} className="bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg h-8" data-testid={`edit-user-${u.id}`}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button size="sm" onClick={() => handleDelete(u.id, u.username)} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg h-8" data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-[#131A38] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#D4AF37]" data-testid="user-dialog-title">
              {editingUser ? 'Edit User' : 'Create New User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Username *</Label>
              <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                placeholder="Enter username" className={inputCls} data-testid="user-username-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">Email *</Label>
              <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                type="email" placeholder="user@example.com" className={inputCls} data-testid="user-email-input" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider mb-1 block">
                {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
              </Label>
              <Input value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                type="password" placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'} className={inputCls} data-testid="user-password-input" />
            </div>
            <div>
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-white/5 rounded-xl border border-white/10">
                <input type="checkbox" checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 accent-[#D4AF37]" data-testid="user-admin-check" />
                <div>
                  <span className="text-white text-sm font-medium">Admin Access</span>
                  <p className="text-[#94A3B8] text-xs">Can manage properties, content, and users</p>
                </div>
              </label>
            </div>
            <Button onClick={handleSave}
              className="w-full bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold h-12 rounded-full" data-testid="save-user-btn">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
